import {
  Box,
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogPositioner,
  DialogRoot,
  Flex,
  HStack,
  Portal,
  Text,
  chakra,
} from "@chakra-ui/react";
import { FormEventHandler, useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import styles from "./VerifyPhoneNumberDialog.module.css";
import { store, useStoreData } from "../../lib/store/store";
import { ActionType } from "../../lib/store/storeActions";
import {
  PublicCredentialRecord,
  UserWithMediaAndGroups,
} from "@greenflagdate/shared";
import { textOptInConsent } from "../../lib/textOptIn";
import { useReq } from "@larner.dev/use-req";
import { apiReq } from "../../lib/apiReq";
import { VerificationCode } from "../VerificationCode/VerificationCode";
import { PhoneField } from "../PhoneField/PhoneField";
import { TextNotificationOptIn } from "../TextNotificationOptIn/TextNotificationOptIn";
import { Icon } from "../../components/Icon/Icon";
import { GhostButton, PrimaryButton } from "../DialogButtons/DialogButtons";
import { getPhoneUtil } from "../../lib/phoneUtil";

interface VerifyPhoneNumberDialogProps {
  isShown: boolean;
  // Fired whenever the dialog is done, success or not. Hide the dialog here.
  // Optional: a caller whose `isShown` is derived from state that onSuccess or
  // onCancel already updates has nothing left to do and can omit it.
  onClose?: () => void;
  // Fired after successful verification, immediately before onClose.
  onSuccess?: () => void;
  // Fired when the user abandons verification (dismissed the dialog without
  // verifying), immediately before onClose. Callers use this to undo work they
  // staged in anticipation of a verified phone — e.g. Settings drops its
  // pending text-enable — without that cleanup also running after a success.
  onCancel?: () => void;
  // Offer first-time verifiers the "text me for everything" opt-in. When
  // checked, the consent rides on the verify request and the server turns
  // text on in the same transaction as the phone save. Off by default: a
  // caller that only needs a verified phone (e.g. Settings, which records
  // consent itself for one category) gets a pure phone-capture dialog.
  offerTextOptIn?: boolean;
}

type ConfirmPhoneNumberErrors = {
  phoneNumber?: string;
};

type VerifyFormErrors = {
  phoneNumber?: string;
  code?: string;
};

function phoneErrorMessage(error?: { code?: string; message?: string } | null) {
  if (!error) {
    return undefined;
  }
  switch (error.code) {
    case "PHONE_IN_USE":
      return "This phone number is already on another Green Flag account. Log in with that account's email, or reach out to us at hi@greenflagdate.com and we'll sort it out.";
    case "INVALID_CODE":
      return "That code didn't match. Double check it and try again.";
    case "MAX_ATTEMPTS":
      return "Too many incorrect attempts. Request a new code to try again.";
    default:
      return error.message;
  }
}

function ErrorBanner({
  title,
  message,
}: {
  title: string;
  message?: string;
}) {
  return (
    <Box
      p="3"
      bg="#FEF5F5"
      borderRadius="xs"
      border="1px solid"
      borderColor="fg.danger"
      mb="3"
    >
      <Text color="fg.danger" fontWeight="bold" fontSize="13px">
        {title}
      </Text>
      {message && (
        <Text color="fg.danger" fontSize="12px" mt="1">
          {message}
        </Text>
      )}
    </Box>
  );
}

export const VerifyPhoneNumberDialog = ({
  isShown,
  onClose,
  onSuccess,
  onCancel,
  offerTextOptIn,
}: VerifyPhoneNumberDialogProps) => {
  const [formStage, setFormStage] = useState(0);
  const storeData = useStoreData();

  // Only first-time verifiers (no verified phone yet) see the text opt-in —
  // re-verifying an existing number shouldn't re-prompt or write a duplicate
  // consent row.
  const showTextOptIn = !!offerTextOptIn && !storeData.user?.phone_number;
  const [optedIn, setOptedIn] = useState(false);

  const [phoneNumberOverride, setPhoneNumberOverride] = useState<string | null>(
    null
  );
  const [parsedUserPhoneNumber, setParsedUserPhoneNumber] = useState<string>("");
  useEffect(() => {
    let cancelled = false;
    const stored = storeData.user?.phone_number_to_validate;
    if (!stored) {
      setParsedUserPhoneNumber("");
      return;
    }
    getPhoneUtil().then(({ phoneUtil }) => {
      if (cancelled) return;
      try {
        const parsed = phoneUtil.parse(stored, "US");
        setParsedUserPhoneNumber(parsed.getNationalNumber()?.toString() ?? "");
      } catch {
        setParsedUserPhoneNumber("");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [storeData.user?.phone_number_to_validate]);
  const phoneNumber =
    phoneNumberOverride !== null ? phoneNumberOverride : parsedUserPhoneNumber;

  const [confirmationErrors, setConfirmationErrors] =
    useState<ConfirmPhoneNumberErrors>({});
  const [setPhoneNumbnerToVerifyRequest, setPhoneNumberToVerifyRequestState] =
    useReq<PublicCredentialRecord>(apiReq);
  const confirmPhoneNumber = useCallback<FormEventHandler>(
    async (e) => {
      e.preventDefault();
      const errors: ConfirmPhoneNumberErrors = {};
      if (!phoneNumber) {
        errors.phoneNumber = "Please enter your phone number";
      }

      const { phoneUtil, libpn } = await getPhoneUtil();
      let parsedPhoneNumber;
      try {
        parsedPhoneNumber = phoneUtil.parse(phoneNumber, "US");
        if (!phoneUtil.isValidNumberForRegion(parsedPhoneNumber, "US")) {
          errors.phoneNumber = "It looks like this phone number is invalid";
        }
      } catch (error) {
        errors.phoneNumber = "It looks like this phone number is invalid";
      }

      setConfirmationErrors(errors);
      if (!Object.keys(errors).length) {
        setPhoneNumberToVerifyRequestState.clearError();
        const formattedPhone = phoneUtil.format(
          parsedPhoneNumber!,
          libpn.PhoneNumberFormat.INTERNATIONAL
        );
        const response = await setPhoneNumbnerToVerifyRequest.post(
          "/user/phone-code",
          {
            phone: formattedPhone,
          }
        );
        if (response.success) {
          store.dispatch({
            type: ActionType.UpdateUser,
            params: {
              user: {
                ...storeData.user!,
                phone_number_to_validate: formattedPhone,
              },
            },
          });

          setFormStage(1);
          setTimeout(() => setFormStage(2), 500);
        }
      }
    },
    [
      phoneNumber,
      setPhoneNumberToVerifyRequestState,
      setPhoneNumbnerToVerifyRequest,
      storeData.user,
    ]
  );

  const [verifyErrors, setVerifyErrors] = useState<VerifyFormErrors>({});
  const [verifyRequest, verifyRequestState] =
    useReq<UserWithMediaAndGroups>(apiReq);
  const [otp, setOtp] = useState("");
  // Reset all transient dialog state, then hand back to the caller. Resetting
  // matters most for optedIn: the component stays mounted across open/close
  // (isShown prop), so without this a checked opt-in from an abandoned session
  // would silently persist and a later verification would record text consent
  // from stale checkbox state rather than an affirmative act in that session.
  const finish = useCallback(
    (outcome: "verified" | "cancelled") => {
      setFormStage(0);
      setConfirmationErrors({});
      setVerifyErrors({});
      setOtp("");
      setOptedIn(false);
      setPhoneNumberToVerifyRequestState.clearError();
      verifyRequestState.clearError();
      if (outcome === "verified") {
        onSuccess?.();
      } else {
        onCancel?.();
      }
      onClose?.();
    },
    [
      setPhoneNumberToVerifyRequestState,
      verifyRequestState,
      onSuccess,
      onCancel,
      onClose,
    ]
  );
  const closeDialog = useCallback(() => finish("cancelled"), [finish]);
  const verify = useCallback(
    async (otpOverride?: string) => {
      const errors: VerifyFormErrors = {};
      const otpVal = otpOverride || otp;

      if (!otpVal) {
        errors.code = "Please enter your code";
      }

      if (!phoneNumber) {
        errors.phoneNumber = "Please enter your phone number";
      }

      const { phoneUtil, libpn } = await getPhoneUtil();
      let parsedPhoneNumber;
      try {
        parsedPhoneNumber = phoneUtil.parse(phoneNumber, "US");
        if (!phoneUtil.isValidNumberForRegion(parsedPhoneNumber, "US")) {
          errors.phoneNumber = "It looks like this phone number is invalid";
        }
      } catch (error) {
        errors.phoneNumber = "It looks like this phone number is invalid";
      }

      if (errors.phoneNumber) {
        setConfirmationErrors({ phoneNumber: errors.phoneNumber });
        setFormStage(1);
        setTimeout(() => setFormStage(0), 500);
      } else if (errors.code) {
        setVerifyErrors(errors);
      } else {
        const formattedPhone = phoneUtil.format(
          parsedPhoneNumber!,
          libpn.PhoneNumberFormat.INTERNATIONAL
        );
        verifyRequestState.clearError();
        const response = await verifyRequest.post(`/user/verify-phone`, {
          code: otpVal,
          phone: formattedPhone,
          ...(showTextOptIn && optedIn
            ? { notification_consent: textOptInConsent("verify-phone-dialog") }
            : {}),
        });

        if (response.success) {
          const { result } = response;
          store.dispatch({
            type: ActionType.UpdateUser,
            params: { user: result },
          });
          finish("verified");
        }
      }
    },
    [
      finish,
      showTextOptIn,
      optedIn,
      otp,
      phoneNumber,
      verifyRequest,
      verifyRequestState,
    ]
  );

  const onOtpChange = useCallback(
    (otpVal: string) => {
      setOtp(otpVal);
      if (otpVal.length === 6) {
        verify(otpVal);
      }
    },
    [verify]
  );

  return (
    <DialogRoot
      open={isShown}
      onOpenChange={(details) => {
        if (!details.open) closeDialog();
      }}
      size="md"
      placement="center"
      motionPreset="slide-in-bottom"
    >
      <Portal>
        <DialogBackdrop zIndex={2000} />
        <DialogPositioner zIndex={2001}>
          <DialogContent
            w={{ base: "calc(100% - 2rem)", md: "28rem" }}
            maxW="28rem"
            mx="auto"
            borderRadius="md"
            bg="bg.canvas"
            display="flex"
            flexDirection="column"
            overflow="hidden"
          >
            <Flex
              align="center"
              justify="space-between"
              px="5"
              py="3"
              borderBottom="1px solid"
              borderColor="border.subtle"
            >
              <Text
                fontSize="11px"
                fontWeight="bold"
                color="fg.muted"
                letterSpacing="0.14em"
                textTransform="uppercase"
              >
                Verify phone number
              </Text>
              <chakra.button
                type="button"
                onClick={closeDialog}
                aria-label="Close"
                bg="transparent"
                border="0"
                p="1"
                cursor="pointer"
                color="fg.muted"
                _hover={{ color: "fg.default" }}
              >
                <Icon name="x" boxSize="14px" />
              </chakra.button>
            </Flex>
            <DialogBody p="5">
              <Box overflow="hidden">
                <Flex
                  className={classNames(
                    styles.formStage,
                    styles[`formStage${formStage}`]
                  )}
                >
                  {[0, 1].includes(formStage) && (
                    <chakra.form
                      flex={1}
                      px="1"
                      boxSizing="border-box"
                      onSubmit={confirmPhoneNumber}
                    >
                      {setPhoneNumberToVerifyRequestState.error && (
                        <ErrorBanner
                          title={
                            setPhoneNumberToVerifyRequestState.error?.code ===
                            "PHONE_IN_USE"
                              ? "This phone number is already in use"
                              : "There was a problem verifying your phone number"
                          }
                          message={phoneErrorMessage(
                            setPhoneNumberToVerifyRequestState.error
                          )}
                        />
                      )}
                      <PhoneField
                        onChange={(e) => setPhoneNumberOverride(e.target.value)}
                        value={phoneNumber}
                        validationMessage={confirmationErrors.phoneNumber}
                        name="phoneNumber"
                      />
                      {showTextOptIn && (
                        <Box mt="4">
                          <TextNotificationOptIn
                            checked={optedIn}
                            onChange={setOptedIn}
                          />
                        </Box>
                      )}
                      <Flex
                        direction={{ base: "column-reverse", sm: "row" }}
                        gap="2"
                        py="6"
                        className={styles.buttonContainer}
                      >
                        <GhostButton
                          flex={1}
                          onClick={closeDialog}
                          disabled={setPhoneNumberToVerifyRequestState.loading}
                        >
                          Cancel
                        </GhostButton>
                        <PrimaryButton
                          type="submit"
                          flex={2}
                          disabled={setPhoneNumberToVerifyRequestState.loading}
                        >
                          Verify
                        </PrimaryButton>
                      </Flex>
                    </chakra.form>
                  )}
                  {[1, 2].includes(formStage) && (
                    <chakra.form
                      flex={1}
                      px="1"
                      boxSizing="border-box"
                      display="flex"
                      flexDirection="column"
                      onSubmit={(e: React.FormEvent<Element>) => {
                        e.preventDefault();
                        verify();
                      }}
                    >
                      <Flex
                        flex={1}
                        direction="column"
                        align="center"
                        justify="center"
                        gap="3"
                      >
                        <Text
                          as="h2"
                          fontSize="18px"
                          fontWeight="semibold"
                          color="fg.default"
                        >
                          Verification Code
                        </Text>
                        <Text
                          fontSize="13px"
                          color="fg.muted"
                          textAlign="center"
                          lineHeight="1.5"
                        >
                          Please check your text messages and enter the code
                          that was sent to you.
                        </Text>
                        {(verifyErrors.code || verifyRequestState.error) && (
                          <ErrorBanner
                            title={
                              verifyRequestState.error?.code === "PHONE_IN_USE"
                                ? "This phone number is already in use"
                                : "There was a problem validating the code"
                            }
                            message={
                              verifyErrors.code ||
                              phoneErrorMessage(verifyRequestState.error)
                            }
                          />
                        )}
                        <VerificationCode
                          value={otp}
                          onChange={onOtpChange}
                          length={6}
                        />
                      </Flex>
                      <HStack gap="2" py="6" justify="stretch">
                        <GhostButton
                          flex={1}
                          onClick={closeDialog}
                          disabled={verifyRequestState.loading}
                        >
                          Cancel
                        </GhostButton>
                        <PrimaryButton
                          type="submit"
                          flex={2}
                          disabled={verifyRequestState.loading}
                        >
                          Sign in
                        </PrimaryButton>
                      </HStack>
                    </chakra.form>
                  )}
                </Flex>
              </Box>
            </DialogBody>
          </DialogContent>
        </DialogPositioner>
      </Portal>
    </DialogRoot>
  );
};
